import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Checkbox,
  Typography,
  Divider,
} from '@mui/material';
import { PrimaryButton } from '@/components/index';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

interface ColumnOrderItem {
  field: string;
  visible: boolean;
}

interface ColumnSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  columnOrder: ColumnOrderItem[];
  getColumnLabel: (field: string) => string;
  onMoveUp: (field: string) => void;
  onMoveDown: (field: string) => void;
  onToggleVisibility: (field: string) => void;
  onReset: () => void;
}

const ColumnSettingsDialog: React.FC<ColumnSettingsDialogProps> = ({
  open,
  onClose,
  columnOrder,
  getColumnLabel,
  onMoveUp,
  onMoveDown,
  onToggleVisibility,
  onReset,
}) => {
  const visibleCount = columnOrder.filter(col => col.visible).length;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Column Settings</Typography>
          <Button
            startIcon={<RestartAltIcon />}
            onClick={onReset}
            size="small"
            variant="outlined"
          >
            Reset to Default
          </Button>
        </Box>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Reorder columns and toggle visibility. At least one column must be visible. The Actions column is always displayed at the end and cannot be reordered.
        </Typography>
        
        <List>
          {columnOrder.map((col, index) => (
            <ListItem
              key={col.field}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 0.75,
                py: 0.25,
                pl: 1,
                pr: 1,
                minHeight: '40px',
              }}
              secondaryAction={
                <Box display="flex" gap={0.5}>
                  <IconButton
                    edge="end"
                    aria-label="move up"
                    onClick={() => onMoveUp(col.field)}
                    disabled={index === 0}
                    size="small"
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="move down"
                    onClick={() => onMoveDown(col.field)}
                    disabled={index === columnOrder.length - 1}
                    size="small"
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </Box>
              }
            >
              <Checkbox
                checked={col.visible}
                onChange={() => onToggleVisibility(col.field)}
                disabled={col.visible && visibleCount === 1}
                tabIndex={-1}
                size="small"
                sx={{ mr: 1 }}
              />
              <ListItemText
                primary={getColumnLabel(col.field)}
                secondary={`Field: ${col.field}`}
                slotProps={{ 
                    primary: { sx: { fontSize: '0.875rem', opacity: col.visible ? 1 : 0.5 } }, 
                    secondary: { sx: { fontSize: '0.75rem', opacity: col.visible ? 0.7 : 0.4 } } 
                }}
              />
            </ListItem>
          ))}
        </List>
        
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          {visibleCount} of {columnOrder.length} columns visible
        </Typography>
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <PrimaryButton onClick={onClose} variant="contained">Close</PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnSettingsDialog;
